import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useStickerSectionStore } from "../EditorStore";
import { BACKEND_URL } from "../../Config";
import { templateBlob } from "../utils/createTemplateBlob";
import { phoneTemplateImages } from "../utils/phoneDetails";


export default function Preview() {
    const navigate = useNavigate();
    const phoneModel = useStickerSectionStore((state) => state.phoneModel);
    const caseFinish = useStickerSectionStore((state) => state.caseFinish);
    const setPreviewImageLinks = useStickerSectionStore((state) => state.setPreviewImageLinks);

    const [loading, setLoading] = useState(false);

    const [cooldown, setCooldown] = useState(0);
    const cooldownRef = useRef<NodeJS.Timeout | null>(null);

    const waitForMockupImage = async (
        url: string,
        maxRetries = 15,
        interval = 2000
    ): Promise<boolean> => {
        for (let i = 0; i < maxRetries; i++) {
            try {
                const res = await fetch(url, { method: "HEAD" });
                if (res.ok) return true;
            } catch { alert("Unable to retrieve mockup.") }
            await new Promise((r) => setTimeout(r, interval));
        }
        return false;
    };

    useEffect(() => {
        if (cooldown > 0) {
            cooldownRef.current = setTimeout(() => {
                setCooldown((prev) => prev - 1);
            }, 1000);
        }

        return () => {
            if (cooldownRef.current) {
                clearTimeout(cooldownRef.current);
            }
        };
    }, [cooldown]);

    const previewPhoneCase = async () => {
        if (cooldown > 0 || !phoneModel || loading) return;

        setLoading(true);

        const variantID = caseFinish === "Matte" ? phoneTemplateImages[phoneModel].matte : phoneTemplateImages[phoneModel].glossy

        try {
            const blob = await templateBlob()
            if (!blob) return
            const formData = new FormData();
            formData.append('file', blob, 'template.png');
            formData.append('variantID', variantID);

            const printifyResponse = await fetch(
                BACKEND_URL + "/preview_printify_product",
                {
                    method: "POST",
                    credentials: "include",
                    body: formData
                }
            );

            if (printifyResponse.status == 429) {
                alert("You can only create a mock every 2 minuets.");
                setLoading(false);
                return;
            }

            const printifyResult = await printifyResponse.json();



            if (
                printifyResponse.ok &&
                Array.isArray(printifyResult.product_image_links)
            ) {
                printifyResult.product_image_links.forEach((url: string) => {
                    const img = new Image();
                    img.src = url;
                });

                const ready = await waitForMockupImage(
                    printifyResult.product_image_links[0]
                );
                if (ready) {
                    setPreviewImageLinks(printifyResult.product_image_links);
                    setCooldown(120);
                    setLoading(false);
                    navigate('/preview');
                } else {
                    alert("Mockup image not ready, please try again later.");
                    setLoading(false);
                }
            } else {
                console.error(printifyResult);
                alert("An error occurred.");
                setLoading(false);
            }
        } catch (err) {
            console.error(err);
            alert("An error occurred." + err);
            setLoading(false);
        }
    };

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    return (
        <button
            onClick={previewPhoneCase}
            disabled={cooldown > 0 || loading}
            style={{
                marginRight: "2%",
                marginLeft: "2%",
                width: "40%",
                height: "80%",
                border: "2px solid white",
                borderRadius: "10px",
                color: "white",
                backgroundColor:
                    cooldown > 0 || loading ? "rgba(100, 100, 100, 0.7)" : "rgba(0, 0, 0, 0.7)",
                cursor: cooldown > 0 || loading ? "not-allowed" : "pointer",
                userSelect: "none",
                pointerEvents: "auto",
            }}
        >
            {loading
                ? "Loading..."
                : cooldown > 0
                    ? `Preview in ${formatTime(cooldown)}`
                    : "Preview Case"}
        </button>
    );
}
